<template>
  <v-form
    :readonly="isReadonly"
    @submit.prevent="() => false"
    ref="form"
    lazy-validation
  >
    <!-- Header -->
    <v-card class="mb-6 elevation-2 rounded-xl">
      <v-card-title class="py-3 px-5 d-flex align-center">
        <v-icon left class="mr-2">mdi-pound</v-icon>
        <span class="text-h6">{{ $t("Group Number") }}</span>
        <v-spacer></v-spacer>
        <v-btn
          small
          text
          :loading="groupCodesLoading"
          @click="fetchGroupCodes"
          class="text-capitalize"
        >
          <v-icon left small>mdi-refresh</v-icon>{{ $t("Refresh") }}
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-6 px-6">
        <v-row>
          <v-col cols="12" md="7" lg="6">
            <div class="form-label d-flex align-center justify-space-between">
              <div>
                {{ $t("Select Group Code") }} <span>*</span>
              </div>
              <div class="d-flex">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-if="canCreate"
                      v-bind="attrs"
                      v-on="on"
                      small
                      outlined
                      color="primary"
                      class="ml-2"
                      @click="openCreateGroup(groupSearch && groupSearch.trim() ? groupSearch.trim() : '')"
                    >
                      <v-icon left small>mdi-plus</v-icon>{{ $t("Create New") }}
                    </v-btn>
                  </template>
                  <span>{{ $t("Create a new group if it doesn't exist") }}</span>
                </v-tooltip>
              </div>
            </div>

            <!-- Group codes dropdown -->
            <v-autocomplete
              :disabled="isReadonly"
              v-model="application.groupCode"
              :items="groupCodes"
              :loading="groupCodesLoading"
              :rules="[$rules.required]"
              :search-input.sync="groupSearch"
              dense
              outlined
              clearable
              hide-selected
              hide-details="auto"
              :label="$t('Choose or search...')"
              prepend-inner-icon="mdi-magnify"
              @change="onGroupChange"
              @blur="onChangeApp"
            >
              <template v-slot:progress>
                <v-progress-linear indeterminate height="2"></v-progress-linear>
              </template>

              <template v-slot:no-data>
                <div class="pa-3">
                  <div class="grey--text mb-2">
                    {{ $t("No matches for") }} <strong>{{ groupSearch }}</strong>
                  </div>
                  <v-btn
                    small
                    color="primary"
                    @click="openCreateGroup(groupSearch ? groupSearch.trim().toUpperCase() : '')"
                  >
                    <v-icon left small>mdi-plus</v-icon>
                    {{ $t("Create") }} “{{ (groupSearch || '').trim() }}”
                  </v-btn>
                </div>
              </template>
            </v-autocomplete>

            <v-slide-y-transition>
              <v-alert
                v-if="groupCodesError"
                type="error"
                dense
                outlined
                class="mt-3"
              >
                {{ groupCodesError }}
              </v-alert>
            </v-slide-y-transition>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Actions -->
    <div class="text-center">
      <v-btn :disabled="isReadonly" @click="onSend" color="accent" large>
        <v-icon left>mdi-content-save</v-icon>{{ $t("Save") }}
      </v-btn>

      <!-- Only offered once the student actually has a group; there is nothing to take them out of
           otherwise. Deliberately does NOT go through onSend: the dropdown is a required field, so an
           empty save would be blocked by its own validation rule. -->
      <v-btn
        v-if="currentGroupCode && !isReadonly"
        :loading="removing"
        class="ml-3"
        color="error"
        outlined
        large
        @click="removeDialog = true"
      >
        <v-icon left>mdi-close-circle-outline</v-icon>{{ $t("Remove Group") }}
      </v-btn>
    </div>

    <app-confirm
      v-model="removeDialog"
      :text="$t('Remove this student from the group?')"
      @accept="onRemoveGroup"
    />

    <!-- Create New Group Dialog -->
    <v-dialog v-model="createDialog" max-width="860" scrollable eager>
      <v-card class="rounded-xl">
        <v-card-title class="py-3">
          <v-icon left class="mr-2">mdi-plus-box</v-icon>
          <span class="text-h6">{{ $t("Create New Group") }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="createDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-6">
          <v-form ref="createForm" lazy-validation>
            <v-row>
              <v-col cols="12" md="4">
                <div class="form-label">
                  {{ $t("Name") }} <span>*</span>
                </div>
                <v-text-field
                  v-model="form.code"
                  :placeholder="$t('e.g. K, M, TK...')"
                  required
                  :rules="[$rules.required]"
                  @input="form.code = (form.code || '').toUpperCase()"
                  autocomplete="off"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12" md="4">
                <div class="form-label">
                  {{ $t("Education Type") }} <span>*</span>
                </div>
                <v-select
                  v-model="form.educationType"
                  :items="lookups.educationTypes"
                  :item-text="lookupText"
                  item-value="id"
                  return-object
                  :loading="lookupsLoading"
                  required
                  :rules="[$rules.required]"
                  outlined
                  dense
                >
                  <template v-slot:prepend-item>
                    <v-list-item>
                      <v-list-item-content class="caption grey--text">
                        {{ $t("Only active items are shown") }}
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mb-1"></v-divider>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="4">
                <div class="form-label">
                  {{ $t("Education Language") }} <span>*</span>
                </div>
                <v-select
                  v-model="form.educationLanguage"
                  :items="lookups.educationLanguages"
                  :item-text="lookupText"
                  item-value="id"
                  return-object
                  :loading="lookupsLoading"
                  required
                  :rules="[$rules.required]"
                  outlined
                  dense
                />
              </v-col>

              <v-col cols="12" md="7">
                <div class="form-label">
                  {{ $t("Education Faculty") }} <span>*</span>
                </div>
                <v-autocomplete
                  v-model="form.educationFaculty"
                  :items="lookups.educationFaculties"
                  :item-text="lookupText"
                  item-value="id"
                  return-object
                  :loading="lookupsLoading"
                  required
                  :rules="[$rules.required]"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>

            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-5">
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false">{{ $t("Cancel") }}</v-btn>
          <v-btn color="accent" :loading="creating" @click="saveNewGroup">
            <v-icon left>mdi-content-save</v-icon>{{ $t("Save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import AppConfirm from "./modals/app-confirm.vue";

export default {
  name: "GroupSelectorWithCreate",
  components: { AppConfirm },
  props: {
    isReadonly: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    isLoading: false,
    isValid: false,
    canCreate: false,

    // dropdown
    groupCodes: [],
    groupCodesLoading: false,
    groupCodesError: null,
    groupSearch: "",

    // remove-from-group
    removeDialog: false,
    removing: false,

    // create dialog
    createDialog: false,
    creating: false,
    lookupsLoading: false,
    form: {
      code: "",
      educationType: null,
      educationLanguage: null,
      educationFaculty: null,
    },

    lookups: {
      educationTypes: [],
      educationLanguages: [],
      educationFaculties: [],
    },
  }),
  computed: {
    ...mapGetters("admin-contract2026", ["GET_APPLICATION"]),
    application: (vm) => vm.GET_APPLICATION,

    // The mapper sends "" rather than null for a contract with no group, so trim before deciding
    // whether there is anything to show or to remove.
    currentGroupCode: (vm) => (vm.application?.groupCode || "").trim(),
  },
  methods: {
    ...mapActions("admin-contract2026", ["SET_APPLICATION", "SAVE_APPLICATION"]),

    onChangeApp() {
      this.$nextTick(() => {
        this.SET_APPLICATION(this.application);
      });
    },

    async onSend() {
      this.isValid = true;
      // Ask the form whether it is valid instead of scanning the whole document for ".error--text".
      // That class is not exclusive to a failed field: Vuetify puts it on ANY element coloured error,
      // and an outlined v-btn is coloured through setTextColor, so the Guruhni olib tashlash button
      // beside this one was picked up as if it were a validation failure and every Save scrolled
      // instead of saving. validate() already returns exactly what this needs.
      const valid = this.$refs.form.validate();
      this.$nextTick(() => {
        if (!valid) {
          // Scoped to an input that is actually in an error state, not to the colour class alone.
          const el = document.querySelector(".v-input.error--text");
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
          return;
        }
        this.SAVE_APPLICATION(this.application).then((status) => {
          if (status) {
            window.location.reload();
          }
        });
      });
    },

    // --- Groups dropdown data ---
    async fetchGroupCodes() {
      this.groupCodesLoading = true;
      this.groupCodesError = null;
      try {
        const { data } = await axios.get(
          "https://api.usat.uz/api/v1/api/groups/get/codes",
          { params: { year: 2026 } }
        );
        // Kept exactly as each group spells its code. Upper-casing them here saved "11/26IQT" onto the
        // contract of a group named "11/26Iqt", and the groups list could then no longer match the two.
        this.groupCodes = Array.isArray(data)
          ? data
              .filter((x) => typeof x === "string")
              .map((s) => s.trim())
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b))
          : [];
        this.keepCurrentGroupSelectable();
      } catch (e) {
        console.error(e);
        this.groupCodesError = e?.response?.data?.message || e?.message || "Failed to load group codes";
        this.$toast?.error?.(this.groupCodesError);
      } finally {
        this.groupCodesLoading = false;
      }
    },

    onGroupChange() {
      this.onChangeApp();
    },

    /**
     * v-autocomplete only renders a selection when the bound value is one of :items, compared exactly.
     * The code stored on the contract is not always its group's spelling (a different case, a stray
     * space, or the upper-cased copy this tab used to save), so it could match nothing and the field came
     * up empty even though the student clearly had a group. Snap the value onto the group's own spelling
     * where there is one, which the next save then writes back, and where the code is not in the list at
     * all (its group row was removed, say) put the stored value in so the student's own group is still
     * what the field shows.
     */
    keepCurrentGroupSelectable() {
      const current = this.currentGroupCode;
      if (!current) return;

      const match = this.groupCodes.find(
        (c) => c.toLowerCase() === current.toLowerCase()
      );
      if (match) {
        if (match !== this.application.groupCode) {
          this.application.groupCode = match;
          this.onChangeApp();
        }
        return;
      }
      this.groupCodes = [current, ...this.groupCodes];
    },

    /**
     * Take this one student out of their group. Only this contract is detached — the group itself is
     * left alone and just has its student counts recomputed on the server.
     */
    async onRemoveGroup() {
      if (this.removing) return;
      this.removing = true;
      try {
        await axios.post(
          "/api/groups/unlink/group/2026",
          null,
          { params: { contractId: this.application.id } }
        );
        window.location.reload();
      } catch (e) {
        console.error("Failed to remove group:", e);
        this.$toast?.error?.(
          e?.response?.data?.message || e?.message || this.$t("Failed to remove group")
        );
        this.removing = false;
      }
    },

    // --- Create New Group flow ---
    async fetchLookups() {
      this.lookupsLoading = true;
      const headers = { "Accept-Language": this.$i18n?.locale || "uz" };
      try {
        const [types, langs, facs] = await Promise.all([
          axios.get("/admin/education-type", { params: { page: 0, size: 100 }, headers }),
          axios.get("/admin/education-language", { params: { page: 0, size: 100 }, headers }),
          axios.get("/admin/faculty", { params: { page: 0, size: 100 }, headers }),
        ]);

        this.lookups.educationTypes     = (types.data?.content  || []).filter((x) => x.active);
        this.lookups.educationLanguages = (langs.data?.content  || []).filter((x) => x.active);
        this.lookups.educationFaculties = (facs.data?.content   || []).filter((x) => x.active);
      } catch (e) {
        console.warn("Lookup fetch failed:", e);
        this.$toast?.error?.(this.$t("Failed to load lookups"));
      } finally {
        this.lookupsLoading = false;
      }
    },

    lookupText(item) {
      const loc = this.$i18n?.locale || "uz";
      return item?.names?.[loc] || item?.names?.uz || item?.names?.ru || item?.names?.en || "";
    },

    openCreateGroup(code) {
      this.form = {
        code: (code || "").toUpperCase().trim(),
        educationType: null,
        educationLanguage: null,
        educationFaculty: null,
      };
      this.createDialog = true;
      // Ensure lookups are loaded
      if (
        !this.lookups.educationTypes.length ||
        !this.lookups.educationLanguages.length ||
        !this.lookups.educationFaculties.length
      ) {
        this.fetchLookups();
      }
      this.$nextTick(() => this.$refs.createForm?.resetValidation?.());
    },

    async saveNewGroup() {
      if (
        !this.form.code ||
        !this.form.educationType ||
        !this.form.educationLanguage ||
        !this.form.educationFaculty
      ) {
        this.$toast?.error?.(this.$t("Please fill in all required fields"));
        return;
      }

      const payload = {
        code: this.form.code.trim().toUpperCase(),
        educationType:     { id: this.form.educationType.id },
        educationLanguage: { id: this.form.educationLanguage.id },
        educationFaculty:  { id: this.form.educationFaculty.id },

        // Always zero on creation
        countOfStudentsInGroup:       1,
        countOfMaleStudentsInGroup:   1,
        countOfFemaleStudentsInGroup: 1,
      };

      try {
        this.creating = true;
        await axios.post("/api/groups", payload); // create only

        // update dropdown + select newly created
        if (!this.groupCodes.includes(payload.code)) {
          this.groupCodes = [...this.groupCodes, payload.code].sort((a, b) => a.localeCompare(b));
        }
        this.application.groupCode = payload.code;
        this.onChangeApp();

        this.$toast?.success?.(this.$t("Group created"));
        this.createDialog = false;
      } catch (e) {
        console.error(e);
        this.$toast?.error?.(this.$t("Failed to save group"));
      } finally {
        this.creating = false;
      }
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  },
  watch: {
    // The codes request and the contract request race: whichever lands second has to be the one that
    // decides what the field shows, or opening the tab straight from a page load leaves it blank.
    currentGroupCode() {
      this.keepCurrentGroupSelectable();
    },
  },
  async mounted() {
    this.canCreate = await this.hasRole("GURUHLAR", "CREATE");
    this.fetchGroupCodes();
  },
};
</script>

<style lang="scss" scoped>
.section-header {
  background: rgb(0, 150, 219);
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.25em;
  color: white;
  padding: 5px;
}

.form-label {
  font-weight: 600;
  margin-bottom: 6px;
  display: inline-flex;
  align-items: center;

  span {
    padding-left: 4px;
    color: #ff5252;
  }
}

.v-card {
  border-radius: 14px;
}

.v-autocomplete,
.v-select,
.v-text-field {
  :deep(.v-input__slot) {
    border-radius: 10px !important;
  }
}

.review-msg {
  position: absolute;
  top: -26px;
}
</style>
